import {
  Box,
  Checkbox,
  RadioGroup as MUIRadioGroup,
  Radio,
  radioClasses,
  SxProps,
  Theme,
} from '@mui/material';

import mixinSx from '@watershed/style/mixinSx';

import SelectableTile, {
  SELECTABLE_TILE_CLASSNAME,
  type SelectableTileProps,
} from '../SelectableTile';

export type SelectableTileGroupOption<ValueType> = SelectableTileProps & {
  value: ValueType;
  dataTest?: string;
};

type CommonPropTypes<ValueType> = {
  options: Array<SelectableTileGroupOption<ValueType>>;
  indeterminateOptionValues?: Array<ValueType>;
  row?: boolean;
  hideSelectAffordance?: boolean;
  numColumns?: number;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  inputTabIndex?: number;
};

type SingleSelectPropType<ValueType extends string | number | boolean | null> =
  {
    value: ValueType | null;
    indeterminateOptionValues?: [];
    onChange: (value: ValueType) => void;
    multiple?: false;
  } & CommonPropTypes<ValueType>;

type MultiSelectPropType<ValueType extends string | number | boolean | null> = {
  value: Array<ValueType>;
  onChange: (value: Array<ValueType>) => void;
  multiple: true;
} & CommonPropTypes<ValueType>;

const tileCn = SELECTABLE_TILE_CLASSNAME;
const labelSx: SxProps<Theme> = {
  width: '100%',
  display: 'flex',
  // So when numColumns > 1, each tile is same height
  height: '100%',
  [`& .${tileCn}`]: {
    borderRadius: 0,
  },
  [`[data-direction="vertical"] &:not(:first-of-type) .${tileCn}`]: {
    marginTop: '-1px',
  },
  [`[data-direction="horizontal"] &:not(:first-of-type) .${tileCn}`]: {
    marginLeft: '-1px',
  },
  [`[data-direction="vertical"] &:not(:last-of-type):has(.${radioClasses.checked}) + & .${tileCn}`]:
    {
      borderTopColor: (theme) => theme.palette.primary.main,
    },
  [`[data-direction="horizontal"] &:not(:last-of-type):has(.${radioClasses.checked}) + & .${tileCn}`]:
    {
      borderLeftColor: (theme) => theme.palette.primary.main,
    },
  [`[data-direction="vertical"] &:first-of-type .${tileCn}`]: {
    borderTopLeftRadius: (theme) => theme.shape.borderRadius,
    borderTopRightRadius: (theme) => theme.shape.borderRadius,
  },
  [`[data-direction="vertical"] &:last-of-type .${tileCn}`]: {
    borderBottomLeftRadius: (theme) => theme.shape.borderRadius,
    borderBottomRightRadius: (theme) => theme.shape.borderRadius,
  },
  [`[data-direction="horizontal"] &:first-of-type .${tileCn}`]: {
    borderTopLeftRadius: (theme) => theme.shape.borderRadius,
    borderBottomLeftRadius: (theme) => theme.shape.borderRadius,
  },
  [`[data-direction="horizontal"] &:last-of-type .${tileCn}`]: {
    borderTopRightRadius: (theme) => theme.shape.borderRadius,
    borderBottomRightRadius: (theme) => theme.shape.borderRadius,
  },
};

export type SelectableTileGroupProps<
  ValueType extends string | number | boolean | null,
> = SingleSelectPropType<ValueType> | MultiSelectPropType<ValueType>;

export default function SelectableTileGroup<
  ValueType extends string | number | boolean | null,
>({
  options,
  indeterminateOptionValues = [],
  value,
  onChange,
  row,
  hideSelectAffordance = false,
  multiple,
  numColumns = 1,
  disabled,
  sx,
  inputTabIndex,
}: SelectableTileGroupProps<ValueType>) {
  return multiple ? (
    <Box
      display="grid"
      gridTemplateColumns={`repeat(${numColumns}, minmax(0, 1fr))`}
      sx={mixinSx({ width: '100%', gap: 0 }, sx)}
      role="checkboxgroup"
      data-direction={numColumns > 1 ? 'horizontal' : 'vertical'}
    >
      {options.map((option) => (
        <Box component="label" key={String(option.value)} sx={labelSx}>
          <SelectableTile
            {...option}
            selected={value?.includes(option.value)}
            disabled={option.disabled || disabled}
            inputAffordance={
              <Checkbox
                tabIndex={inputTabIndex ?? 0}
                sx={hideSelectAffordance ? { display: 'none' } : undefined}
                value={option.value}
                checked={value?.includes(option.value)}
                indeterminate={indeterminateOptionValues.includes(option.value)}
                disabled={option.disabled || disabled}
                onChange={(_, checked) => {
                  checked
                    ? onChange([...(value ?? []), option.value])
                    : onChange((value ?? []).filter((v) => v !== option.value));
                }}
                data-testid={
                  option.dataTest ?? `radioGroupOption-${option.value}`
                }
              />
            }
          />
        </Box>
      ))}
    </Box>
  ) : (
    <MUIRadioGroup
      onChange={(_, value) => onChange(value as ValueType)}
      value={value}
      row={row}
      sx={mixinSx(
        {
          flexWrap: 'nowrap',
          alignItems: 'flex-start',
          width: '100%',
          gap: 0,
          gridTemplateColumns: `repeat(${numColumns}, minmax(0, 1fr))`,
          display: 'grid',
        },
        sx
      )}
      data-direction={numColumns > 1 ? 'horizontal' : 'vertical'}
    >
      {options.map((option) => (
        <Box
          component="label"
          key={String(option.value)}
          sx={labelSx}
          data-testid={`radioGroupOption-${option.value}`}
        >
          <SelectableTile
            {...option}
            disabled={option.disabled || disabled}
            selected={value === option.value}
            inputAffordance={
              <Radio
                tabIndex={inputTabIndex ?? 0}
                sx={hideSelectAffordance ? { display: 'none' } : undefined}
                value={option.value}
                disabled={option.disabled || disabled}
              />
            }
          />
        </Box>
      ))}
    </MUIRadioGroup>
  );
}
